import { R } from '@/modules/api/actions'
import { AdminClientOnboarding } from '@storefronts/common-types'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useEffectiveClientHash } from '@/modules/auth/useEffectiveClientHash'

type BrandSetupContextType = {
    onboarding: AdminClientOnboarding | null
    updateOnboarding: (onboarding: AdminClientOnboarding) => Promise<void>
    refreshOnboarding: () => Promise<void>
    loading: boolean
}

export const BrandSetupContext = createContext<BrandSetupContextType>({
    onboarding: null,
    updateOnboarding: async () => {},
    refreshOnboarding: async () => {},
    loading: false,
})

export const BrandSetupContextProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const clientHash = useEffectiveClientHash()
    const fetchRequestIdRef = useRef(0)
    const [onboarding, setOnboarding] = useState<AdminClientOnboarding | null>(
        null,
    )
    const [loading, setLoading] = useState<boolean>(!!clientHash)

    const fetchOnboarding = useCallback(async () => {
        if (!clientHash) {
            setLoading(false)
            return
        }

        const requestId = ++fetchRequestIdRef.current
        R.setClient(clientHash)
        setLoading(true)
        setOnboarding(null)
        const response = await R.getClientOnboarding()
        if (fetchRequestIdRef.current !== requestId) return
        if (response.isError) {
            console.error('Failed to fetch onboarding:', response.message)
        } else {
            setOnboarding(response.result.onboarding)
        }
        if (fetchRequestIdRef.current === requestId) setLoading(false)
    }, [clientHash])

    const updateOnboarding = useCallback(
        async (nextOnboarding: AdminClientOnboarding) => {
            setLoading(true)
            const response = await R.updateClientOnboarding(nextOnboarding)
            if (response.isError) {
                console.error('Failed to update onboarding:', response.message)
            } else {
                setOnboarding(response.result.onboarding)
            }
            setLoading(false)
        },
        [],
    )

    const refreshOnboarding = fetchOnboarding

    useEffect(() => {
        fetchOnboarding()
    }, [fetchOnboarding])

    return (
        <BrandSetupContext.Provider
            value={{ onboarding, updateOnboarding, refreshOnboarding, loading }}
        >
            {children}
        </BrandSetupContext.Provider>
    )
}
